import React, { useState, useEffect } from "react";
import type { ClinicalRecord } from "../../types/shared";

interface SectionCardProps {
  title: string;
  icon: React.ReactNode;
  children?: React.ReactNode;
  emptyMsg?: string;
}

interface ItemRowProps {
  label?: React.ReactNode;
  detail?: React.ReactNode;
  sub?: React.ReactNode;
  onFlag?: (display: React.ReactNode) => void;
}

interface FlagTarget {
  type: string;
  display: React.ReactNode;
}

function SectionCard({ title, icon, children, emptyMsg }: SectionCardProps) {
  return (
    <div style={{
      backgroundColor: "#fff",
      borderRadius: 12,
      padding: "14px 18px",
      border: "1px solid #e6dccb",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        paddingBottom: 8, marginBottom: 10,
        borderBottom: "2px solid #e6dccb",
      }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "#182a33", margin: 0 }}>{title}</h3>
      </div>
      {children || (
        <p style={{ fontSize: 13, color: "#b0b0b0", fontStyle: "italic", margin: 0 }}>{emptyMsg || "No data available"}</p>
      )}
    </div>
  );
}

function ItemRow({ label, detail, sub, onFlag }: ItemRowProps) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "6px 0", borderBottom: "1px solid #f5f0e8",
    }}>
      <div style={{ minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: "#182a33" }}>{label}</span>
        {detail && <span style={{ fontSize: 12, color: "#555", marginLeft: 8 }}>{detail}</span>}
        {sub && <div style={{ fontSize: 11, color: "#5b6e7c", marginTop: 1 }}>{sub}</div>}
      </div>
      {onFlag && (
        <button
          onClick={() => onFlag(label)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 10, color: "#c9772a", opacity: 0.6, padding: "2px 4px", flexShrink: 0,
          }}
          title="Flag an issue"
        >
          Flag
        </button>
      )}
    </div>
  );
}

interface ClinicalChartSectionProps {
  patientId?: string;
}

export default function ClinicalChartSection({ patientId }: ClinicalChartSectionProps) {
  const [record, setRecord] = useState<ClinicalRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [flagging, setFlagging] = useState<FlagTarget | null>(null);
  const [flagNote, setFlagNote] = useState("");
  const [flagType, setFlagType] = useState("correction");
  const [flagSubmitted, setFlagSubmitted] = useState(false);

  useEffect(() => {
    if (!patientId) { setLoading(false); return; }
    fetch(`/api/patient/${encodeURIComponent(patientId)}/clinical-record`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.exists) setRecord(data.record);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [patientId]);

  function handleSubmitFlag() {
    if (!flagging || !flagNote.trim()) return;
    fetch(`/api/patient/${encodeURIComponent(patientId as string)}/clinical-record/flags`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        item_type: flagging.type,
        item_display: flagging.display,
        flag_type: flagType,
        note: flagNote.trim(),
      }),
    })
      .then(() => {
        setFlagging(null);
        setFlagNote("");
        setFlagSubmitted(true);
        setTimeout(() => setFlagSubmitted(false), 4000);
      })
      .catch(() => {});
  }

  const flag = (type: string) => (display: React.ReactNode) => setFlagging({ type, display });

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 0", color: "#5b6e7c" }}>Loading clinical chart...</div>;
  }

  if (!patientId) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0", color: "#5b6e7c" }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>📋</div>
        <p style={{ fontSize: 16 }}>Set up your profile to view your clinical chart</p>
      </div>
    );
  }

  const conditions = (record?.conditions || []).filter(c => c.status === "active");
  const currentMeds = record?.medications?.current || [];
  const previousMeds = (record?.medications?.previous || record?.medications?.discontinued || []) as any[];
  const allergies = (record?.allergies || []) as any[];
  const observations = (record?.observations || []) as any[];
  const vitals = (record?.vitals || []) as any[];
  const socialHistory = (record?.social_history || {}) as Record<string, unknown>;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#182a33", margin: 0 }}>Clinical Chart</h2>
        <p style={{ fontSize: 13, color: "#5b6e7c", margin: "4px 0 0" }}>
          Your health record from your provider. Flag any issues for your doctor to review.
        </p>
      </div>

      {/* Flag notifications */}
      {flagSubmitted && (
        <div style={{
          backgroundColor: "#eef4f2", borderRadius: 8, padding: 12, marginBottom: 16,
          border: "1px solid #bbd5cc", fontSize: 13, color: "#6b9c8e", fontWeight: 500,
        }}>
          Flag submitted! Your provider will review it.
        </div>
      )}

      {flagging && (
        <div style={{
          backgroundColor: "#fdf4ea", borderRadius: 8, padding: 14, marginBottom: 16,
          border: "1px solid #fae4ca",
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#844e1b", marginBottom: 8 }}>
            Flag issue with: {flagging.display}
          </div>
          <select
            value={flagType}
            onChange={e => setFlagType(e.target.value)}
            style={{ fontSize: 12, padding: "6px 8px", borderRadius: 4, border: "1px solid #d8c9b6", marginBottom: 6, width: "100%" }}
          >
            <option value="correction">Incorrect information</option>
            <option value="stopped">I stopped this medication</option>
            <option value="new">I started a new medication</option>
            <option value="other">Other</option>
          </select>
          <textarea
            value={flagNote}
            onChange={e => setFlagNote(e.target.value)}
            placeholder="Describe the issue..."
            rows={2}
            style={{ fontSize: 12, padding: 8, borderRadius: 4, border: "1px solid #d8c9b6", width: "100%", resize: "vertical", boxSizing: "border-box" }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              onClick={handleSubmitFlag}
              style={{ fontSize: 12, fontWeight: 600, padding: "6px 16px", borderRadius: 6, border: "none", backgroundColor: "#182a33", color: "white", cursor: "pointer" }}
            >
              Submit Flag
            </button>
            <button
              onClick={() => { setFlagging(null); setFlagNote(""); }}
              style={{ fontSize: 12, padding: "6px 16px", borderRadius: 6, border: "1px solid #d8c9b6", backgroundColor: "white", color: "#666", cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Past Medical History */}
        <SectionCard title="Past Medical History" icon="🩺" emptyMsg="No conditions on file">
          {conditions.length > 0 && conditions.map((c, i) => (
            <ItemRow
              key={i}
              label={c.display}
              sub={[c.icd10 && `ICD-10: ${c.icd10}`, c.onset && `Since ${c.onset}`].filter(Boolean).join(" · ")}
              onFlag={flag("condition")}
            />
          ))}
        </SectionCard>

        {/* Allergies */}
        <SectionCard title="Allergies" icon="⚠️" emptyMsg="No known allergies">
          {allergies.length > 0 && allergies.map((a, i) => (
            <ItemRow
              key={i}
              label={a.allergen || a.display}
              detail={a.reaction}
              sub={a.severity && `Severity: ${a.severity}`}
              onFlag={flag("allergy")}
            />
          ))}
        </SectionCard>

        {/* Current Medications */}
        <SectionCard title="Current Medications" icon="💊" emptyMsg="No current medications">
          {currentMeds.length > 0 && currentMeds.map((m, i) => (
            <ItemRow
              key={i}
              label={m.display}
              detail={[m.dose, m.frequency].filter(Boolean).join(" ")}
              sub={[m.atc && `ATC: ${m.atc}`, m.din && `DIN: ${m.din}`].filter(Boolean).join(" · ")}
              onFlag={flag("medication")}
            />
          ))}
        </SectionCard>

        {/* Previous Medications */}
        <SectionCard title="Previous Medications" icon="📦" emptyMsg="No previous medications">
          {previousMeds.length > 0 && previousMeds.map((m, i) => (
            <ItemRow
              key={i}
              label={m.display}
              detail={m.dose}
              sub={[m.reason_stopped && `Reason: ${m.reason_stopped}`, m.end_date && `Stopped: ${m.end_date}`].filter(Boolean).join(" · ")}
            />
          ))}
        </SectionCard>

        {/* Vitals */}
        <SectionCard title="Vitals" icon="❤️" emptyMsg="No vitals recorded">
          {vitals.length > 0 && vitals.map((v, i) => {
            const latest = Array.isArray(v.values) ? v.values[0] : v;
            return (
              <ItemRow
                key={i}
                label={v.display || v.concept_id}
                detail={latest ? `${latest.value}${latest.unit ? ` ${latest.unit}` : ""}` : ""}
                sub={latest?.date}
              />
            );
          })}
        </SectionCard>

        {/* Labs / Observations */}
        <SectionCard title="Labs & Results" icon="🧪" emptyMsg="No lab results on file">
          {observations.length > 0 && observations.map((obs, i) => {
            const latest = obs.values?.[0];
            return (
              <ItemRow
                key={i}
                label={obs.display}
                detail={latest ? `${latest.value}${latest.unit ? ` ${latest.unit}` : ""}` : ""}
                sub={[obs.loinc && `LOINC: ${obs.loinc}`, latest?.date].filter(Boolean).join(" · ")}
                onFlag={flag("observation")}
              />
            );
          })}
        </SectionCard>

        {/* Social History */}
        {Object.keys(socialHistory).length > 0 && (
          <div style={{ gridColumn: "1 / -1" }}>
            <SectionCard title="Social History" icon="🏠">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                {Object.entries(socialHistory).map(([key, value]) => (
                  <ItemRow key={key} label={key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} detail={String(value)} />
                ))}
              </div>
            </SectionCard>
          </div>
        )}
      </div>

      <div style={{ fontSize: 10, color: "#b0b0b0", textAlign: "right", marginTop: 12 }}>
        Data from your provider's records. Flag any issues for your doctor to review.
      </div>
    </div>
  );
}
